/**
 * Phone Extraction History Tab Component
 *
 * Displays history of phone extraction tasks for a project
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Card,
  Empty,
  Progress,
  Tooltip,
} from 'antd'
import {
  ReloadOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table/interface'
import api from '@/services/api'

const { Text } = Typography

interface PhoneExtractionHistoryTabProps {
  projectId: number
}

interface ExtractionTask {
  id: number
  rq_job_id: string | null
  project_id: number
  parsing_task_id: string | null
  status: string
  progress: number
  current_step: string | null
  total_urls: number
  processed_urls: number
  total_phones_extracted: number
  successful_extractions: number
  failed_extractions: number
  extraction_methods: string[] | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  pending: { color: 'warning', icon: <ClockCircleOutlined />, label: 'В очереди' },
  running: { color: 'processing', icon: <SyncOutlined spin />, label: 'Выполняется' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, label: 'Завершено' },
  failed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Ошибка' },
  cancelled: { color: 'default', icon: <ExclamationCircleOutlined />, label: 'Отменено' },
}

const PhoneExtractionHistoryTab = forwardRef(({ projectId }: PhoneExtractionHistoryTabProps, ref) => {
  const [tasks, setTasks] = useState<ExtractionTask[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  const fetchTasks = async (page: number = 1, pageSize: number = 20) => {
    if (!projectId) return

    setLoading(true)

    try {
      const response = await api.getPhoneExtractionTasks({
        project_id: projectId,
        page,
        page_size: pageSize,
      })

      setTasks(response.items || [])
      setPagination({
        current: response.page || page,
        pageSize: response.page_size || pageSize,
        total: response.total || 0,
      })
    } catch (error) {
      console.error('Fetch extraction tasks error:', error)
      message.error('Ошибка загрузки истории')
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    refresh: () => fetchTasks(pagination.current, pagination.pageSize),
  }))

  useEffect(() => {
    fetchTasks()
  }, [projectId])

  // Auto-refresh for running tasks
  useEffect(() => {
    const hasRunningTasks = tasks.some(t => t.status === 'running' || t.status === 'pending')
    if (hasRunningTasks) {
      const interval = setInterval(() => {
        fetchTasks(pagination.current, pagination.pageSize)
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [tasks, pagination.current, pagination.pageSize])

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination({
      ...pagination,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || pagination.pageSize,
    })
    fetchTasks(newPagination.current || 1, newPagination.pageSize || pagination.pageSize)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    } catch {
      return '—'
    }
  }

  const columns: ColumnsType<ExtractionTask> = [
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => (
        <Text style={{ fontSize: '12px', fontFamily: 'monospace' }}>{id}</Text>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Статус</span>,
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => {
        const config = statusConfig[status] || statusConfig.pending
        return (
          <Tag color={config.color} icon={config.icon} style={{ margin: 0, fontSize: '11px' }}>
            {config.label}
          </Tag>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Прогресс</span>,
      key: 'progress',
      width: 150,
      render: (_: any, record: ExtractionTask) => (
        <div>
          <Progress
            percent={record.progress || 0}
            size="small"
            status={
              record.status === 'failed' ? 'exception' :
              record.status === 'completed' ? 'success' :
              'active'
            }
            style={{ marginBottom: 4 }}
          />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.processed_urls}/{record.total_urls} URL
          </Text>
        </div>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Телефоны</span>,
      key: 'phones',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: ExtractionTask) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
            <PhoneOutlined style={{ marginRight: 4 }} />
            {record.total_phones_extracted}
          </Text>
          {record.failed_extractions > 0 && (
            <Text type="danger" style={{ fontSize: '10px' }}>
              {record.failed_extractions} ошибок
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Методы</span>,
      dataIndex: 'extraction_methods',
      key: 'extraction_methods',
      width: 120,
      render: (methods: string[] | null) => (
        methods ? (
          <Space size={2} wrap>
            {methods.map(m => (
              <Tag key={m} color="blue" style={{ margin: 0, fontSize: '10px' }}>
                {m}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: '11px' }}>—</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Шаг</span>,
      dataIndex: 'current_step',
      key: 'current_step',
      ellipsis: true,
      render: (step: string | null, record: ExtractionTask) => (
        record.error_message ? (
          <Tooltip title={record.error_message}>
            <Text type="danger" style={{ fontSize: '12px' }}>
              {record.error_message.substring(0, 50)}...
            </Text>
          </Tooltip>
        ) : (
          <Text style={{ fontSize: '12px' }}>{step || '—'}</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Начало</span>,
      dataIndex: 'started_at',
      key: 'started_at',
      width: 150,
      render: (date: string | null) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {formatDate(date)}
        </Text>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Завершение</span>,
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 150,
      render: (date: string | null) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {formatDate(date)}
        </Text>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <PhoneOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            <Text strong style={{ fontSize: '14px' }}>История сбора телефонов</Text>
            <Tag color="blue">{pagination.total}</Tag>
          </Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => fetchTasks(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Обновить
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card
        style={{
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {tasks.length === 0 && !loading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет задач по сбору телефонов"
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={tasks}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total: number) => `Всего: ${total}`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            rowKey="id"
            size="small"
          />
        )}
      </Card>
    </div>
  )
})

PhoneExtractionHistoryTab.displayName = 'PhoneExtractionHistoryTab'

export default PhoneExtractionHistoryTab
